<template>
  <div class="vendor-group-list-container vendor-group-list-main vendor-group-list-page--glass glass-tab-page">
    <BrucdcumHeader :breadcrumb="['Company', 'Vendor Group List']" />

    <div class="glass-tab-view glass-tab-view--flush vendor-group-list-glass-shell">
      <div class="glass-tab-view__content vendor-group-list-tab-content vendor-group-list-page-stack">
        <div class="vendor-group-list-header-block">
          <div class="vendor-group-list-header-row">
            <div class="vendor-group-list-header-row__text">
              <h2>Vendor Groups</h2>
              <p class="vendor-group-list-header-row__sub">Create and manage groups for organizing vendors</p>
            </div>
            <div class="vendor-group-list-header-actions">
              <PrimaryButton label="Add New" @click="showModal = true" />
            </div>
          </div>
        </div>

        <div class="vendor-group-list-glass-panel vendor-group-list-main-card">
          <div class="consultant-glass-table-section vendor-group-list-table-section">
            <div
              class="consultant-glass-table-responsive consultant-glass-table-responsive--framed"
              :class="{ 'consultant-glass-table-responsive--loading': loading }"
            >
              <div class="consultant-glass-table-scroll-x">
                <table class="consultant-glass-table vendor-group-list-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th class="vendor-group-list-delete-header">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="loading">
                      <td colspan="2" class="consultant-glass-table-no-records vendor-group-list-no-records">
                        Loading...
                      </td>
                    </tr>
                    <tr v-else-if="error">
                      <td colspan="2" class="consultant-glass-table-no-records vendor-group-list-no-records">{{ error }}</td>
                    </tr>
                    <tr v-else-if="groups.length === 0">
                      <td colspan="2" class="consultant-glass-table-no-records vendor-group-list-no-records">
                        No records to show
                      </td>
                    </tr>
                    <template v-else>
                      <tr
                        v-for="(group, idx) in groups"
                        :key="group.id || idx"
                        class="consultant-glass-table-row vendor-group-list-row"
                        :style="{ '--vgl-row-delay': `${0.04 + Math.min(idx, 12) * 0.03}s` }"
                      >
                        <td>{{ group.name }}</td>
                        <td class="vendor-group-list-delete-cell">
                          <SecondaryButton
                            label="Delete"
                            icon="bi bi-trash"
                            custom-class="consultant-secondary-btn--danger vendor-group-list-delete-btn"
                            @click="openDeleteModal(idx)"
                          />
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <AddNewVendorGroup
      :visible="showModal"
      :companyId="companyId"
      @ok="handleVendorGroupCreated"
      @cancel="closeModal"
    />

    <SuccessModal
      :show="showSuccessModal"
      title="Success"
      :message="successMessage"
      buttonText="OK"
      @close="closeSuccessModal"
    />

    <ErrorModal
      :show="showErrorModal"
      title="Error"
      :message="errorMessage"
      buttonText="OK"
      @close="closeErrorModal"
    />

    <WarningModal
      :show="showWarningModal"
      title="Delete Vendor Group"
      :message="warningMessage"
      buttonText="Cancel"
      confirmButtonText="Delete"
      @close="closeWarningModal"
      @confirm="confirmDeleteGroup"
    />
  </div>
</template>

<script>
import '../styles/VendorGroupList.css';
import AddNewVendorGroup from './AddNewVendorGroup.vue';
import logger from '@/utils/logger';
import CompanyService from '@/services/company.service.js';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import WarningModal from '@/components/WarningModal.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'VendorGroupList',
  components: {
    AddNewVendorGroup,
    BrucdcumHeader,
    SuccessModal,
    ErrorModal,
    WarningModal,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      showModal: false,
      groups: [],
      loading: false,
      error: '',
      companyId: this.$route.params.id,
      showSuccessModal: false,
      showErrorModal: false,
      showWarningModal: false,
      errorMessage: '',
      warningMessage: '',
      successMessage: '',
      deleteIdx: null,
      isDeleting: false,
    };
  },
  computed: {
    hasGroups() {
      return this.groups && this.groups.length > 0;
    },
    isLoading() {
      return this.loading || this.isDeleting;
    },
    canDelete() {
      return this.hasGroups && !this.isLoading;
    },
  },
  async mounted() {
    await this.fetchGroups();
  },
  methods: {
    async fetchGroups() {
      this.loading = true;
      this.error = '';
      try {
        this.groups = await CompanyService.getAllVendorGroups(this.companyId);
        logger.log('Successfully fetched vendor groups:', this.groups.length);
      } catch (err) {
        logger.error('Error fetching vendor groups:', err);

        if (err.response?.data?.message) {
          this.errorMessage = err.response.data.message;
        } else if (err.response?.data?.error) {
          this.errorMessage = err.response.data.error;
        } else if (err.response?.data?.detail) {
          this.errorMessage = err.response.data.detail;
        } else if (err.response?.status === 404) {
          this.errorMessage = 'Vendor groups service not found. Please contact support.';
        } else if (err.response?.status === 403) {
          this.errorMessage = 'You do not have permission to view vendor groups.';
        } else if (err.response?.status === 500) {
          this.errorMessage =
            'Internal server error occurred while fetching vendor groups. The server encountered an unexpected condition. Please try again in a few minutes.';
        } else if (err.response?.status === 502) {
          this.errorMessage =
            'Bad gateway error. The server received an invalid response from another server. Please try again later.';
        } else if (err.response?.status === 503) {
          this.errorMessage =
            'Service temporarily unavailable. The server is currently unable to handle the request. Please try again later.';
        } else if (err.response?.status === 504) {
          this.errorMessage =
            'Gateway timeout. The server did not receive a timely response. Please try again later.';
        } else if (err.response?.status >= 500) {
          this.errorMessage = `Server error (${err.response.status}) occurred while fetching vendor groups. Please try again later or contact support if the problem persists.`;
        } else if (err.code === 'NETWORK_ERROR' || !err.response) {
          this.errorMessage = 'Network connection error. Please check your internet connection and try again.';
        } else if (err.message) {
          this.errorMessage = err.message;
        } else {
          this.errorMessage = 'An unexpected error occurred while fetching vendor groups. Please try again.';
        }
        this.showErrorModal = true;
      } finally {
        this.loading = false;
      }
    },
    closeModal() {
      this.showModal = false;
    },
    async handleVendorGroupCreated(createdGroup) {
      this.closeModal();
      await this.fetchGroups();
      const groupCount = this.groups.length;
      this.successMessage = `Vendor group "${createdGroup?.name || 'New Vendor Group'}" created successfully! You now have ${groupCount} vendor group${groupCount !== 1 ? 's' : ''} in your system.`;
      this.showSuccessModal = true;
      logger.log('Vendor group created successfully, total groups:', groupCount);
    },
    openDeleteModal(idx) {
      const group = this.groups[idx];
      if (!group || !group.id) {
        this.errorMessage = 'Invalid group selected. Please refresh the page and try again.';
        this.showErrorModal = true;
        return;
      }

      if (!group.name || group.name.trim() === '') {
        this.errorMessage = 'Cannot delete a group with an empty name.';
        this.showErrorModal = true;
        return;
      }

      this.deleteIdx = idx;
      this.warningMessage = `Are you sure you want to delete the vendor group "${group.name}"? This action cannot be undone and may affect vendors assigned to this group.`;
      this.showWarningModal = true;
      logger.log('Delete confirmation requested for vendor group:', group.name);
    },

    closeWarningModal() {
      this.showWarningModal = false;
      this.deleteIdx = null;
      this.warningMessage = '';
    },

    async confirmDeleteGroup() {
      if (this.deleteIdx !== null) {
        const group = this.groups[this.deleteIdx];
        if (!group || !group.id) return;

        this.isDeleting = true;
        this.showWarningModal = false;

        try {
          logger.log('Attempting to delete vendor group:', group.name, 'ID:', group.id);
          await CompanyService.deleteVendorGroup(group.id);
          this.groups.splice(this.deleteIdx, 1);
          this.error = '';

          const remainingCount = this.groups.length;
          this.successMessage = `Vendor group "${group.name}" has been deleted successfully. You now have ${remainingCount} vendor group${remainingCount !== 1 ? 's' : ''} remaining.`;
          this.showSuccessModal = true;
          logger.log('Vendor group deleted successfully, remaining groups:', remainingCount);
        } catch (err) {
          logger.error('Error deleting vendor group:', err);

          const errorText =
            err.response?.data?.message ||
            err.response?.data?.error ||
            err.response?.data?.detail ||
            err.message ||
            '';

          if (
            errorText.includes('foreign key constraint') ||
            errorText.includes('violates foreign key') ||
            errorText.includes('referenced by') ||
            errorText.includes('cannot delete') ||
            errorText.includes('constraint') ||
            errorText.includes('vendors') ||
            errorText.includes('vendorGroupId') ||
            errorText.includes('VendorGroups')
          ) {
            if (
              errorText.includes('vendors') ||
              errorText.includes('vendorGroupId') ||
              errorText.includes('VendorGroups') ||
              errorText.includes('vendor')
            ) {
              this.errorMessage = `Cannot delete "${group.name}" because it is currently assigned to one or more vendors. Please reassign those vendors to another group first.`;
            } else {
              this.errorMessage = `Cannot delete "${group.name}" because it is currently being used by other records. Please reassign those records to another group first.`;
            }
          } else if (err.response?.data?.message) {
            this.errorMessage = err.response.data.message;
          } else if (err.response?.data?.error) {
            this.errorMessage = err.response.data.error;
          } else if (err.response?.data?.detail) {
            this.errorMessage = err.response.data.detail;
          } else if (err.response?.status === 409) {
            this.errorMessage = `Cannot delete "${group.name}" because it is currently assigned to one or more vendors. Please reassign those vendors to another group first.`;
          } else if (err.response?.status === 404) {
            this.errorMessage = 'Vendor group not found. It may have already been deleted.';
          } else if (err.response?.status === 403) {
            this.errorMessage = 'You do not have permission to delete vendor groups.';
          } else if (err.response?.status === 500) {
            const serverErrorText =
              err.response?.data?.message || err.response?.data?.error || err.response?.data?.detail || '';
            if (
              serverErrorText.includes('foreign key constraint') ||
              serverErrorText.includes('violates foreign key') ||
              serverErrorText.includes('referenced by') ||
              serverErrorText.includes('vendors') ||
              serverErrorText.includes('vendorGroupId') ||
              serverErrorText.includes('VendorGroups')
            ) {
              this.errorMessage = `Cannot delete "${group.name}" because it is currently assigned to one or more vendors. Please reassign those vendors to another group first.`;
            } else {
              this.errorMessage =
                'Internal server error occurred while deleting the vendor group. The server encountered an unexpected condition. Please try again in a few minutes.';
            }
          } else if (err.response?.status === 502) {
            this.errorMessage =
              'Bad gateway error. The server received an invalid response from another server. Please try again later.';
          } else if (err.response?.status === 503) {
            this.errorMessage =
              'Service temporarily unavailable. The server is currently unable to handle the request. Please try again later.';
          } else if (err.response?.status === 504) {
            this.errorMessage = 'Gateway timeout. The server did not receive a timely response. Please try again later.';
          } else if (err.response?.status >= 500) {
            this.errorMessage = `Server error (${err.response.status}) occurred while deleting the vendor group. Please try again later or contact support if the problem persists.`;
          } else if (err.code === 'NETWORK_ERROR' || !err.response) {
            this.errorMessage = 'Network connection error. Please check your internet connection and try again.';
          } else if (err.message) {
            this.errorMessage = err.message;
          } else {
            this.errorMessage = `An unexpected error occurred while deleting "${group.name}". Please try again.`;
          }
          this.showErrorModal = true;
        } finally {
          this.isDeleting = false;
          this.deleteIdx = null;
          this.warningMessage = '';
        }
      }
    },

    closeSuccessModal() {
      this.showSuccessModal = false;
    },

    closeErrorModal() {
      this.showErrorModal = false;
      this.errorMessage = '';
    },
  },
};
</script>
